<template :class="coachEffectClass" :key="$store.state.calculatorRerender">
	<v-carousel v-if="!$store.state.loading && allLayers.length > 0" :key="carouselKey" hide-delimiter-background progress height="100vh" :continuous="false"
		:next-icon="showCarouselNextButton ? undefined : false"
		v-model="currentCarouselIndex"
		@change="onCarouselChange">
		<template v-for="layer in allLayers">
			<v-carousel-item v-if="layer.shuffle_cards" :class="`carousel`"
				:key="`shuffle-${layer.id}`">
				<Card v-if="activeShuffleCards[layer.id]" :card="activeShuffleCards[layer.id]"
					:shuffleCards="shuffleCards" :layer="layer" :featured="true" />
			</v-carousel-item>
			<v-carousel-item v-for="card in allCards(layer.cards, layer)" :key="`card-${card.id}`"
				:class="`carousel`" v-else>
				<PostStep :card="card" v-if="card.type.includes('poststep')"
					@next-button-visibility="onPostStepNextButtonVisibility(card.id, $event)" />
				<Card :card="card" :layer="layer" :featured="true" v-else />
			</v-carousel-item>
		</template>
	</v-carousel>
</template>

<script>
import Card from '@/components/Card.vue'
import PostStep from '@/components/PostStep.vue';

export default {
	components: { Card, PostStep },
	data() {
		return {
			activeShuffleCards: {},
			currentCarouselIndex: 0,
			postStepNextButtonStates: {}, // Track next button visibility state per PostStep card
			showCarouselNextButton: true, // Default to showing next button
		}
	},
	watch: {
		'$store.state.endUser.language': {
			handler() {
				// Reset and reinitialize shuffle cards on language change
				this.activeShuffleCards = {}
				this.allLayers.forEach(layer => {
					if (layer.shuffle_cards) {
						this.shuffleCards(layer)
					}
				})
			},
			immediate: true
		},
		allLayers: {
			handler(newLayers) {
				// Update shuffle cards for new layers
				newLayers.forEach(layer => {
					if (layer.shuffle_cards && !this.activeShuffleCards[layer.id]) {
						this.shuffleCards(layer)
					}
				})

				// Clean up shuffle cards for removed layers
				Object.keys(this.activeShuffleCards).forEach(layerId => {
					if (!newLayers.find(l => l.id == layerId)) {
						this.$delete(this.activeShuffleCards, layerId)
					}
				})

				this.$nextTick(() => {
					this.updateCarouselNextButton()
				})
			},
			immediate: true
		},
	},
	methods: {
		cards(cards) {
			return cards.filter(c => {
				return !c.hidden
					&& !c.isFeatured
					&& !c.type.includes('coach')
					&& !c.type.includes('prestep')
					&& !c.type.includes('menu_bottom_banner')
					&& (!c.lang || c.lang === this.$store.state.endUser.language)
			})
		},

		shuffleCards(layer) {
			let shuffledCard = this.cards(layer.cards).sort(() => Math.random() - 0.5)[0];
			while (shuffledCard === this.activeShuffleCards[layer.id]) {
				shuffledCard = this.cards(layer.cards).sort(() => Math.random() - 0.5)[0];
			}
			this.$set(this.activeShuffleCards, layer.id, shuffledCard);
		},

		featuredCards(cards) {
			return cards.filter(c => {
				return !c.hidden
					&& c.isFeatured
					&& !c.type.includes('coach')
					&& !c.type.includes('prestep')
					&& !c.type.includes('menu_bottom_banner')
					&& (!c.lang || c.lang === this.$store.state.endUser.language)
			})
		},

		postStepCards(cards) {
			return cards.filter(c => {
				return !c.hidden
					&& c.type.includes('poststep')
			})
		},

		allCards(cards, layer) {
			// If layer is not active, only return poststep cards (that's why it's in allLayers)
			// If layer is active, return all cards
			if (layer && !layer.active) {
				return this.postStepCards(cards)
			}
			return [...this.featuredCards(cards), ...this.cards(cards)]
		},

		onPostStepNextButtonVisibility(cardId, shouldShow) {
			this.$set(this.postStepNextButtonStates, cardId, shouldShow)
			this.updateCarouselNextButton()
		},

		onCarouselChange(index) {
			this.currentCarouselIndex = index
			this.updateCarouselNextButton()
		},

		updateCarouselNextButton() {
			// Get all carousel items (flattened from all layers)
			const allCarouselItems = []
			this.allLayers.forEach(layer => {
				if (layer.shuffle_cards) {
					allCarouselItems.push({ type: 'shuffle', layerId: layer.id })
				} else {
					this.allCards(layer.cards, layer).forEach(card => {
						allCarouselItems.push({
							type: card.type.includes('poststep') ? 'poststep' : 'card',
							card
						})
					})
				}
			})

			// Check if current item is a PostStep
			const currentItem = allCarouselItems[this.currentCarouselIndex]
			if (currentItem && currentItem.type === 'poststep' && currentItem.card) {
				this.showCarouselNextButton = this.postStepNextButtonStates[currentItem.card.id] !== false
			} else {
				// For non-PostStep items (shuffle or regular cards), show the next button
				this.showCarouselNextButton = true
			}
		},
	},

	computed: {
		coachEffectClass() {
			return this.$store.state.coachEffect
		},

		carouselKey() {
			// Create a unique key based on active layers to force carousel re-render when layers change
			return `carousel-${this.allLayers.map(l => l.id).join(',')}`
		},

		allLayers() {
			// Reactivity trigger for language changes
			let endUserLanguage = this.$store.state.endUser.language

			return this.$store.state.layers
				.filter(l => l.active || this.postStepCards(l.cards).length > 0)
				.filter(l => !l.hidden)
		},
	},

	mounted() {
		// Initialize shuffle cards for layers that have shuffle_cards set to true
		this.allLayers.forEach(layer => {
			if (layer.shuffle_cards) {
				this.shuffleCards(layer);
			}
		});

		this.$nextTick(() => {
			this.updateCarouselNextButton();
		})
	}
}
</script>
